import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { eventBus } from '@/services/eventBus';
import type {
  CulturalContext,
  Location,
  SensoryProfile,
  World,
  WorldCapabilities,
  WorldOverview,
  WorldRules
} from '@/types';
import type { ChapterWorldContext } from '@/types/storyContext';
import type { WorldAIReadiness } from '@/types/worldAIReadiness';

interface CreateWorldInput {
  overview?: Partial<WorldOverview>;
  rules?: Partial<WorldRules>;
  locations?: Location[];
  culture?: Partial<CulturalContext>;
  capabilities?: Partial<WorldCapabilities>;
  notes?: string;
  aiReadiness?: WorldAIReadiness;
}

interface UpdateWorldInput {
  overview?: WorldOverview;
  rules?: WorldRules;
  locations?: Location[];
  culture?: CulturalContext;
  capabilities?: WorldCapabilities;
  aiReadiness?: WorldAIReadiness;
  notes?: string;
}

type WorldCollection = Record<string, World>;

const generateId = (prefix: string): string => {
  const hasCrypto = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function';
  if (hasCrypto) {
    return crypto.randomUUID();
  }
  const randomSuffix = Math.random().toString(36).slice(2, 10);
  return `${prefix}-${randomSuffix}`;
};

type WorldReadinessModule = typeof import('@/services/worldAIReadinessService');
let worldReadinessServiceInstance: WorldReadinessModule['worldAIReadinessService'] | null = null;

const loadWorldReadinessService = async (): Promise<
  WorldReadinessModule['worldAIReadinessService']
> => {
  if (!worldReadinessServiceInstance) {
    const module = await import('@/services/worldAIReadinessService');
    worldReadinessServiceInstance = module.worldAIReadinessService;
  }
  return worldReadinessServiceInstance;
};

const cloneSensoryProfile = (profile: SensoryProfile): SensoryProfile => ({
  visualStyle: profile.visualStyle,
  sounds: profile.sounds,
  smells: profile.smells,
  tactileFeeling: profile.tactileFeeling,
  notableFeatures: [...profile.notableFeatures],
  timeOfDayChanges: profile.timeOfDayChanges,
  weatherImpact: profile.weatherImpact
});

const cloneLocation = (location: Location): Location => ({
  ...location,
  sensoryProfile: cloneSensoryProfile(location.sensoryProfile),
  chapterIds: location.chapterIds ? [...location.chapterIds] : undefined,
  characterIds: location.characterIds ? [...location.characterIds] : undefined,
  uiState: { ...location.uiState }
});

const cloneRules = (rules: WorldRules): WorldRules => ({
  physics: {
    gravity: rules.physics.gravity,
    dayNightCycle: rules.physics.dayNightCycle,
    weather: rules.physics.weather,
    majorDeviations: [...rules.physics.majorDeviations]
  },
  technologyLevel: {
    era: rules.technologyLevel.era,
    notableAdvances: [...rules.technologyLevel.notableAdvances],
    notableLimitations: [...rules.technologyLevel.notableLimitations],
    everydayTech: [...rules.technologyLevel.everydayTech]
  },
  magicSystem: rules.magicSystem
    ? {
        exists: rules.magicSystem.exists,
        commonness: rules.magicSystem.commonness,
        source: rules.magicSystem.source,
        cost: rules.magicSystem.cost,
        limitations: [...rules.magicSystem.limitations],
        capabilities: [...rules.magicSystem.capabilities],
        socialPerception: rules.magicSystem.socialPerception
      }
    : undefined,
  impossibilities: [...rules.impossibilities],
  consequences: rules.consequences
});

const cloneCulture = (culture: CulturalContext): CulturalContext => ({
  socialHierarchy: culture.socialHierarchy,
  powerStructures: culture.powerStructures,
  expectations: [...culture.expectations],
  taboos: [...culture.taboos],
  currencyAndTrade: culture.currencyAndTrade,
  commonOccupations: [...culture.commonOccupations],
  languages: [...culture.languages],
  communicationStyle: culture.communicationStyle,
  coreValues: [...culture.coreValues],
  commonBeliefs: [...culture.commonBeliefs],
  majorConflicts: [...culture.majorConflicts]
});

const cloneCapabilities = (capabilities: WorldCapabilities): WorldCapabilities => ({
  travelMethods: [...capabilities.travelMethods],
  travelTime: capabilities.travelTime,
  communicationMethods: [...capabilities.communicationMethods],
  communicationSpeed: capabilities.communicationSpeed,
  informationAvailability: capabilities.informationAvailability,
  recordKeeping: capabilities.recordKeeping,
  medicalCapabilities: capabilities.medicalCapabilities,
  lifespanNorms: capabilities.lifespanNorms,
  privacyLevel: capabilities.privacyLevel,
  weaponsAccess: capabilities.weaponsAccess
});

const cloneReadiness = (readiness: WorldAIReadiness | undefined): WorldAIReadiness | undefined => {
  if (!readiness) {
    return undefined;
  }

  return {
    ...readiness,
    dimensions: {
      clarity: { ...readiness.dimensions.clarity },
      sensory: { ...readiness.dimensions.sensory },
      consistency: { ...readiness.dimensions.consistency },
      locations: { ...readiness.dimensions.locations }
    },
    recommendations: readiness.recommendations.map((item) => ({ ...item })),
    missingCritical: [...readiness.missingCritical]
  };
};

const cloneWorld = (world: World): World => ({
  ...world,
  overview: { ...world.overview },
  rules: cloneRules(world.rules),
  locations: world.locations.map((location) => cloneLocation(location)),
  culture: cloneCulture(world.culture),
  capabilities: cloneCapabilities(world.capabilities),
  aiReadiness: cloneReadiness(world.aiReadiness)
});

const createDefaultOverview = (): WorldOverview => ({
  name: '',
  genre: '',
  timePeriod: '',
  geographicalScope: 'regional',
  realismLevel: 'grounded',
  tone: '',
  uniqueHook: ''
});

const createDefaultRules = (): WorldRules => ({
  physics: {
    gravity: 'earth-like',
    dayNightCycle: 'earth-like',
    weather: 'earth-like',
    majorDeviations: []
  },
  technologyLevel: {
    era: 'modern',
    notableAdvances: [],
    notableLimitations: [],
    everydayTech: []
  },
  magicSystem: undefined,
  impossibilities: [],
  consequences: ''
});

const createDefaultCulture = (): CulturalContext => ({
  socialHierarchy: '',
  powerStructures: '',
  expectations: [],
  taboos: [],
  currencyAndTrade: '',
  commonOccupations: [],
  languages: [],
  communicationStyle: '',
  coreValues: [],
  commonBeliefs: [],
  majorConflicts: []
});

const createDefaultCapabilities = (): WorldCapabilities => ({
  travelMethods: [],
  travelTime: '',
  communicationMethods: [],
  communicationSpeed: '',
  informationAvailability: '',
  recordKeeping: '',
  medicalCapabilities: '',
  lifespanNorms: '',
  privacyLevel: '',
  weaponsAccess: ''
});

const createWorldEntity = (manuscriptId: string, input: CreateWorldInput | undefined): World => {
  const payload = input ?? {};
  const now = new Date().toISOString();
  const overview = { ...createDefaultOverview(), ...(payload.overview ?? {}) };
  const rules = cloneRules({ ...createDefaultRules(), ...(payload.rules ?? {}) });
  const culture = cloneCulture({ ...createDefaultCulture(), ...(payload.culture ?? {}) });
  const capabilities = cloneCapabilities({
    ...createDefaultCapabilities(),
    ...(payload.capabilities ?? {})
  });
  return {
    id: generateId('world'),
    manuscriptId,
    overview,
    rules,
    locations: (payload.locations ?? []).map((location) => cloneLocation(location)),
    culture,
    capabilities,
    aiReadiness: cloneReadiness(payload.aiReadiness),
    createdAt: now,
    updatedAt: now,
    notes: payload.notes ?? ''
  };
};

const createDefaultSensoryProfile = (): SensoryProfile => ({
  visualStyle: '',
  sounds: '',
  smells: '',
  tactileFeeling: '',
  notableFeatures: [],
  timeOfDayChanges: '',
  weatherImpact: ''
});

const createLocationEntity = (world: World, payload: Partial<Location>): Location => {
  const now = new Date().toISOString();
  const sensory = payload.sensoryProfile
    ? {
        visualStyle: payload.sensoryProfile.visualStyle ?? '',
        sounds: payload.sensoryProfile.sounds ?? '',
        smells: payload.sensoryProfile.smells ?? '',
        tactileFeeling: payload.sensoryProfile.tactileFeeling ?? '',
        notableFeatures: payload.sensoryProfile.notableFeatures
          ? [...payload.sensoryProfile.notableFeatures]
          : [],
        timeOfDayChanges: payload.sensoryProfile.timeOfDayChanges ?? '',
        weatherImpact: payload.sensoryProfile.weatherImpact ?? ''
      }
    : createDefaultSensoryProfile();

  return {
    id: generateId('location'),
    worldId: world.id,
    name: payload.name ?? '',
    type: payload.type ?? 'public',
    importance: payload.importance ?? 'secondary',
    sensoryProfile: sensory,
    size: payload.size ?? 'modest',
    accessibility: payload.accessibility ?? '',
    typicalOccupants: payload.typicalOccupants ?? '',
    atmosphere: payload.atmosphere ?? '',
    storyRole: payload.storyRole ?? '',
    chapterIds: payload.chapterIds ? [...payload.chapterIds] : [],
    characterIds: payload.characterIds ? [...payload.characterIds] : [],
    uiState: payload.uiState ? { ...payload.uiState } : { isExpanded: false },
    createdAt: now,
    updatedAt: now,
    notes: payload.notes ?? ''
  };
};

export const useWorldStore = defineStore('world', () => {
  const worldsByManuscript = ref<WorldCollection>({});
  const activeManuscriptId = ref<string | null>(null);
  const isLoading = ref(false);
  const lastError = ref<string | null>(null);
  const pendingMutations = ref(0);
  const aiReadinessCache = ref<Map<string, WorldAIReadiness>>(new Map());

  const beginMutation = (): void => {
    pendingMutations.value += 1;
    isLoading.value = true;
    lastError.value = null;
  };

  const finishMutation = (): void => {
    pendingMutations.value = Math.max(pendingMutations.value - 1, 0);
    if (pendingMutations.value === 0) {
      isLoading.value = false;
    }
  };

  const captureErrorMessage = (error: unknown, fallback: string): string => {
    if (error instanceof Error && error.message.trim().length > 0) {
      lastError.value = error.message;
      return error.message;
    }
    lastError.value = fallback;
    return fallback;
  };

  const writeWorld = (manuscriptId: string, world: World): void => {
    worldsByManuscript.value = { ...worldsByManuscript.value, [manuscriptId]: world };
  };

  const clearReadinessForWorld = (manuscriptId: string, world: World): World => {
    aiReadinessCache.value.delete(world.id);
    const updated: World = { ...world, aiReadiness: undefined };
    writeWorld(manuscriptId, updated);
    eventBus.emit('aiReadiness:changed', {
      manuscriptId,
      system: 'world',
      entityId: world.id,
      readiness: null
    });
    return updated;
  };

  const setReadinessForWorld = (
    manuscriptId: string,
    world: World,
    readiness: WorldAIReadiness
  ): World => {
    aiReadinessCache.value.set(world.id, readiness);
    const updated: World = { ...world, aiReadiness: cloneReadiness(readiness) };
    writeWorld(manuscriptId, updated);
    eventBus.emit('aiReadiness:changed', {
      manuscriptId,
      system: 'world',
      entityId: world.id,
      readiness: readiness.overall ?? null
    });
    return updated;
  };

  const activeWorld = computed<World | null>(() => {
    if (!activeManuscriptId.value) {
      return null;
    }
    const world = worldsByManuscript.value[activeManuscriptId.value];
    if (!world) {
      return null;
    }
    return cloneWorld(world);
  });

  const hasActiveWorld = computed<boolean>(() => activeWorld.value !== null);

  const requireActiveManuscript = (): string => {
    if (!activeManuscriptId.value) {
      console.error('World store operation requires an active manuscript.', {
        context: 'requireActiveManuscript'
      });
      throw new Error('No active manuscript selected for world operations.');
    }
    return activeManuscriptId.value;
  };

  const setActiveManuscript = (manuscriptId: string | null): void => {
    activeManuscriptId.value = manuscriptId;
  };

  const createWorld = (input?: CreateWorldInput): World => {
    const manuscriptId = requireActiveManuscript();
    beginMutation();
    try {
      const world = createWorldEntity(manuscriptId, input);
      const linkedWorld: World = {
        ...world,
        locations: world.locations.map((location) => ({
          ...location,
          worldId: world.id
        }))
      };
      aiReadinessCache.value.delete(linkedWorld.id);
      writeWorld(manuscriptId, linkedWorld);
      return cloneWorld(linkedWorld);
    } catch (error) {
      console.error('Failed to create world.', { error, manuscriptId, input });
      const message = captureErrorMessage(error, 'Unable to create world.');
      throw error instanceof Error ? error : new Error(message);
    } finally {
      finishMutation();
    }
  };

  const createLocation = (payload: Partial<Location>): Location => {
    const manuscriptId = requireActiveManuscript();
    const existing = worldsByManuscript.value[manuscriptId];
    if (!existing) {
      throw new Error('No world data found for the active manuscript.');
    }
    beginMutation();
    try {
      const location = createLocationEntity(existing, payload);
      const updated: World = {
        ...existing,
        locations: [...existing.locations, location],
        updatedAt: new Date().toISOString()
      };
      clearReadinessForWorld(manuscriptId, updated);
      return cloneLocation(location);
    } catch (error) {
      console.error('Failed to create location.', { error, payload });
      const message = captureErrorMessage(error, 'Unable to create location.');
      throw error instanceof Error ? error : new Error(message);
    } finally {
      finishMutation();
    }
  };

  const updateLocation = (locationId: string, updates: Partial<Location>): Location => {
    const manuscriptId = requireActiveManuscript();
    const existing = worldsByManuscript.value[manuscriptId];
    if (!existing) {
      throw new Error('No world data found for the active manuscript.');
    }
    const index = existing.locations.findIndex((location) => location.id === locationId);
    if (index === -1) {
      throw new Error('Location not found.');
    }
    const current = existing.locations[index];
    beginMutation();
    try {
      const merged: Location = {
        ...current,
        ...updates,
        sensoryProfile: updates.sensoryProfile
          ? {
              visualStyle: updates.sensoryProfile.visualStyle ?? current.sensoryProfile.visualStyle,
              sounds: updates.sensoryProfile.sounds ?? current.sensoryProfile.sounds,
              smells: updates.sensoryProfile.smells ?? current.sensoryProfile.smells,
              tactileFeeling:
                updates.sensoryProfile.tactileFeeling ?? current.sensoryProfile.tactileFeeling,
              notableFeatures: updates.sensoryProfile.notableFeatures
                ? [...updates.sensoryProfile.notableFeatures]
                : [...current.sensoryProfile.notableFeatures],
              timeOfDayChanges:
                updates.sensoryProfile.timeOfDayChanges ?? current.sensoryProfile.timeOfDayChanges,
              weatherImpact:
                updates.sensoryProfile.weatherImpact ?? current.sensoryProfile.weatherImpact
            }
          : cloneSensoryProfile(current.sensoryProfile),
        updatedAt: new Date().toISOString()
      };
      const locations = [...existing.locations];
      locations[index] = merged;
      const updated: World = {
        ...existing,
        locations,
        updatedAt: new Date().toISOString()
      };
      clearReadinessForWorld(manuscriptId, updated);
      return cloneLocation(merged);
    } catch (error) {
      console.error('Failed to update location.', { error, locationId, updates });
      const message = captureErrorMessage(error, 'Unable to update location.');
      throw error instanceof Error ? error : new Error(message);
    } finally {
      finishMutation();
    }
  };

  const deleteLocation = (locationId: string): void => {
    const manuscriptId = requireActiveManuscript();
    const existing = worldsByManuscript.value[manuscriptId];
    if (!existing) {
      throw new Error('No world data found for the active manuscript.');
    }
    beginMutation();
    try {
      const updatedLocations = existing.locations.filter((location) => location.id !== locationId);
      const updated: World = {
        ...existing,
        locations: updatedLocations,
        updatedAt: new Date().toISOString()
      };
      clearReadinessForWorld(manuscriptId, updated);
    } catch (error) {
      console.error('Failed to delete location.', { error, locationId });
      const message = captureErrorMessage(error, 'Unable to delete location.');
      throw error instanceof Error ? error : new Error(message);
    } finally {
      finishMutation();
    }
  };

  const duplicateLocation = (locationId: string): Location | null => {
    const manuscriptId = requireActiveManuscript();
    const existing = worldsByManuscript.value[manuscriptId];
    if (!existing) {
      throw new Error('No world data found for the active manuscript.');
    }
    const template = existing.locations.find((location) => location.id === locationId);
    if (!template) {
      return null;
    }
    beginMutation();
    try {
      const duplicate: Location = {
        ...cloneLocation(template),
        id: generateId('location'),
        name: `${template.name} (Copy)`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      const updated: World = {
        ...existing,
        locations: [...existing.locations, duplicate],
        updatedAt: new Date().toISOString()
      };
      clearReadinessForWorld(manuscriptId, updated);
      return cloneLocation(duplicate);
    } catch (error) {
      console.error('Failed to duplicate location.', { error, locationId });
      const message = captureErrorMessage(error, 'Unable to duplicate location.');
      throw error instanceof Error ? error : new Error(message);
    } finally {
      finishMutation();
    }
  };

  const setLocationExpanded = (locationId: string, expanded: boolean): void => {
    const manuscriptId = requireActiveManuscript();
    const existing = worldsByManuscript.value[manuscriptId];
    if (!existing) {
      throw new Error('No world data found for the active manuscript.');
    }
    const locations = existing.locations.map((location) =>
      location.id === locationId
        ? {
            ...location,
            uiState: {
              ...location.uiState,
              isExpanded: expanded
            }
          }
        : location
    );
    const updated: World = {
      ...existing,
      locations,
      updatedAt: existing.updatedAt
    };
    writeWorld(manuscriptId, updated);
  };

  const updateWorld = (updates: UpdateWorldInput): World => {
    const manuscriptId = requireActiveManuscript();
    const existing = worldsByManuscript.value[manuscriptId];
    if (!existing) {
      console.error('Attempted to update a world that does not exist.', {
        manuscriptId,
        updates
      });
      throw new Error('No world data found for the active manuscript.');
    }
    beginMutation();
    try {
      const updatedOverview = updates.overview ? { ...updates.overview } : existing.overview;
      const updatedRules = updates.rules ? cloneRules(updates.rules) : cloneRules(existing.rules);
      const updatedLocations = updates.locations
        ? updates.locations.map((location) => cloneLocation(location))
        : existing.locations.map((location) => cloneLocation(location));
      const updatedCulture = updates.culture
        ? cloneCulture(updates.culture)
        : cloneCulture(existing.culture);
      const updatedCapabilities = updates.capabilities
        ? cloneCapabilities(updates.capabilities)
        : cloneCapabilities(existing.capabilities);
      const updatedWorldId = existing.id;
      const updated: World = {
        ...existing,
        overview: updatedOverview,
        rules: updatedRules,
        locations: updatedLocations.map((location) => ({
          ...location,
          worldId: updatedWorldId
        })),
        culture: updatedCulture,
        capabilities: updatedCapabilities,
        notes: updates.notes ?? existing.notes,
        updatedAt: new Date().toISOString()
      };
      const result = updates.aiReadiness
        ? setReadinessForWorld(manuscriptId, updated, updates.aiReadiness)
        : clearReadinessForWorld(manuscriptId, updated);
      return cloneWorld(result);
    } catch (error) {
      console.error('Failed to update world.', { error, updates });
      const message = captureErrorMessage(error, 'Unable to update world.');
      throw error instanceof Error ? error : new Error(message);
    } finally {
      finishMutation();
    }
  };

  const updateOverview = (partial: Partial<WorldOverview>): WorldOverview => {
    const manuscriptId = requireActiveManuscript();
    const existing = worldsByManuscript.value[manuscriptId];
    if (!existing) {
      console.error('Attempted to update world overview without existing world data.', {
        manuscriptId,
        partial
      });
      throw new Error('Cannot update overview before a world exists.');
    }
    beginMutation();
    try {
      const mergedOverview: WorldOverview = { ...existing.overview, ...partial };
      const updated: World = {
        ...existing,
        overview: mergedOverview,
        updatedAt: new Date().toISOString()
      };
      clearReadinessForWorld(manuscriptId, updated);
      return { ...mergedOverview };
    } catch (error) {
      console.error('Failed to update overview.', { error, partial });
      const message = captureErrorMessage(error, 'Unable to update overview.');
      throw error instanceof Error ? error : new Error(message);
    } finally {
      finishMutation();
    }
  };

  const recalculateWorldReadiness = async (): Promise<WorldAIReadiness | null> => {
    const manuscriptId = activeManuscriptId.value;
    if (!manuscriptId) {
      return null;
    }
    const existing = worldsByManuscript.value[manuscriptId];
    if (!existing) {
      return null;
    }
    beginMutation();
    try {
      const readinessService = await loadWorldReadinessService();
      const readiness = readinessService.calculateWorldReadiness(existing);
      setReadinessForWorld(manuscriptId, existing, readiness);
      return cloneReadiness(readiness) ?? null;
    } catch (error) {
      console.error('Failed to recalculate world readiness.', { error, manuscriptId });
      const message = captureErrorMessage(error, 'Unable to recalculate world readiness.');
      throw error instanceof Error ? error : new Error(message);
    } finally {
      finishMutation();
    }
  };

  const getWorldReadiness = async (): Promise<WorldAIReadiness | null> => {
    const manuscriptId = activeManuscriptId.value;
    if (!manuscriptId) {
      return null;
    }
    const existing = worldsByManuscript.value[manuscriptId];
    if (!existing) {
      return null;
    }
    const readinessService = await loadWorldReadinessService();
    const cached = aiReadinessCache.value.get(existing.id);
    if (cached && readinessService.isCacheValid(cached)) {
      const readinessClone = cloneReadiness(cached);
      if (readinessClone) {
        writeWorld(manuscriptId, { ...existing, aiReadiness: readinessClone });
      }
      return readinessClone ?? null;
    }
    return await recalculateWorldReadiness();
  };

  const removeWorld = (manuscriptId: string): void => {
    const existing = worldsByManuscript.value[manuscriptId];
    if (!existing) {
      return;
    }
    beginMutation();
    try {
      const updated = { ...worldsByManuscript.value };
      delete updated[manuscriptId];
      worldsByManuscript.value = updated;
      aiReadinessCache.value.delete(existing.id);
    } finally {
      finishMutation();
    }
  };

  const clearManuscript = (manuscriptId: string): void => {
    const existing = worldsByManuscript.value[manuscriptId];
    if (existing) {
      aiReadinessCache.value.delete(existing.id);
      const clone = { ...worldsByManuscript.value };
      delete clone[manuscriptId];
      worldsByManuscript.value = clone;
    }
    if (activeManuscriptId.value === manuscriptId) {
      activeManuscriptId.value = null;
    }
  };

  const resetStore = (): void => {
    worldsByManuscript.value = {};
    activeManuscriptId.value = null;
    aiReadinessCache.value.clear();
    isLoading.value = false;
    lastError.value = null;
    pendingMutations.value = 0;
  };

  const getWorldByManuscript = (manuscriptId: string): World | null => {
    const world = worldsByManuscript.value[manuscriptId];
    if (!world) {
      return null;
    }
    return cloneWorld(world);
  };

  const buildChapterWorldContext = (): ChapterWorldContext | null => {
    const current = activeWorld.value;
    if (!current) {
      return null;
    }
    return {
      overview: { ...current.overview },
      rules: cloneRules(current.rules),
      culture: cloneCulture(current.culture),
      capabilities: cloneCapabilities(current.capabilities),
      locations: current.locations.map((location) => ({
        id: location.id,
        name: location.name,
        type: location.type,
        importance: location.importance,
        storyRole: location.storyRole,
        sensoryProfile: {
          visualStyle: location.sensoryProfile.visualStyle,
          sounds: location.sensoryProfile.sounds,
          smells: location.sensoryProfile.smells,
          tactileFeeling: location.sensoryProfile.tactileFeeling,
          notableFeatures: [...location.sensoryProfile.notableFeatures],
          timeOfDayChanges: location.sensoryProfile.timeOfDayChanges,
          weatherImpact: location.sensoryProfile.weatherImpact
        }
      })),
      constraints: {
        impossibilities: [...current.rules.impossibilities],
        consequences: current.rules.consequences
      },
      readiness: current.aiReadiness ? cloneReadiness(current.aiReadiness) : undefined
    };
  };

  return {
    activeWorld,
    hasActiveWorld,
    isLoading,
    pendingMutations,
    lastError,
    setActiveManuscript,
    createWorld,
    createLocation,
    updateLocation,
    deleteLocation,
    duplicateLocation,
    setLocationExpanded,
    updateWorld,
    updateOverview,
    recalculateWorldReadiness,
    getWorldReadiness,
    removeWorld,
    clearManuscript,
    resetStore,
    getWorldByManuscript,
    buildChapterWorldContext
  };
});

